import { useFetcher } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import useUserPreferences from '#app/hooks/use-user-preferences'
import { StoreSelectorDrawer } from './store-selector-drawer'
import { type SelectedStoresCookie } from '.'

const MobileStoreSelector = (props: {
	totalCount: number
	brandParams: string[]
}) => {
	const userPreferences = useUserPreferences()
	return userPreferences?.selectedStores?.length > 0 ? (
		<MobileSelectedStore
			selectedStores={userPreferences.selectedStores as SelectedStoresCookie[]}
			totalCount={props.totalCount}
		/>
	) : (
		<MobileSelectStore {...props} />
	)
}

const MobileSelectStore = ({
	totalCount,
	brandParams,
}: {
	totalCount: number
	brandParams: string[]
}) => {
	let { t } = useTranslation('product_list_page')
	return (
		<div className="flex flex-col items-start justify-between space-y-1 rounded-md bg-beige-50-opacity px-4 py-2 md:flex-row md:items-center  md:space-y-0 md:px-[var(--plenty-padding)]">
			<div className="flex flex-row space-x-1">
				<PlentyBody size="md">{totalCount}</PlentyBody>
				<div className="w-full overflow-x-auto whitespace-nowrap scrollbar-hide">
					{brandParams.length > 0 && (
						<PlentyBody size="md">
							{brandParams.length > 1 ? (
								t('products_from_brands', { count: brandParams.length })
							) : (
								<span
									dangerouslySetInnerHTML={{
										__html: t('products_from_brand', { brand: brandParams[0] }),
									}}
								></span>
							)}
						</PlentyBody>
					)}

					{brandParams.length === 0 && (
						<PlentyBody size="md">{t('product_count_text')}</PlentyBody>
					)}
				</div>
			</div>
			<StoreSelectorDrawer selectedStores={[]} />
		</div>
	)
}

const MobileSelectedStore = ({
	selectedStores,
	totalCount,
}: {
	selectedStores: SelectedStoresCookie[]
	totalCount: number
}) => {
	let { t } = useTranslation('product_list_page')
	const { trackAmplitudeEvent } = useAmplitudeContext()
	const removeSelectedStoreFetcher = useFetcher()
	const removeSelectedStore = () => {
		trackAmplitudeEvent('remove selected stores')
		removeSelectedStoreFetcher.submit(null, {
			method: 'POST',
			action: '/resources/removeSelectedStores',
		})
	}

	return (
		<div className="flex flex-col justify-between space-y-1 bg-beige-50 px-4 py-2 md:flex-row md:space-y-0 md:px-[var(--plenty-padding)]">
			<div className="flex flex-row items-center space-x-2">
				<PlentyBody>
					{t('mobile_store_selector_products_count', { totalCount })}
					{selectedStores.length === 1
						? t('mobile_store_selector_products_from_store', {
								storeName: selectedStores[0].name,
						  })
						: t('mobile_store_selector_products_from_stores', {
								storeCount: selectedStores.length,
						  })}
				</PlentyBody>

				<Button variant="link" onClick={removeSelectedStore}>
					<Icon name="close" size="sm" />
				</Button>
			</div>
			<StoreSelectorDrawer selectedStores={selectedStores} />
		</div>
	)
}

export default MobileStoreSelector
